import { useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { Button, Callout, PageHeader, SwitchToggle } from "@components/ui_v2";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useBuroReconsultationMutation } from "@api/sat_apis_new/satApsi";
import { STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { setModalErrorVisible, setProcessPage } from "@store/slices/appSlice";
import { setCheckSatBureau } from "@store/slices/registerSlice";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@interfaces/response";

const BURO_URL = "https://www.burodecredito.com.mx/personas-físicas/productos/bloqueo.html";

export const BuroBloqueado = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const shareholderData = useSelector((state: RootState) => state.register.shareholder);
  const companyData = useSelector((state: RootState) => state.register.company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [confirmUnlock, setConfirmUnlock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [buroReconsultation] = useBuroReconsultationMutation();

  const isPfae = applicationData.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;
  const rfc = isPfae ? companyData.rfc : shareholderData.rfc + shareholderData.rfc_homoclave;

  const handleClick = async () => {
    try {
      setIsLoading(true);
      await buroReconsultation({
        rfc,
        loan_application_id: applicationData.id,
        application_code: applicationData.company_application_code,
        person_type: "PF",
      }).unwrap();

      const { code: next_status, url: next_url } = STATUS_CATALOG.waiting_room;

      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: next_status,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setProcessPage(next_url));
      dispatch(setCheckSatBureau({ check_sat_bureau: true }));
      navigate(next_url);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "BuroBloqueado",
          values: { rfc, loan_application_id: applicationData.id },
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Consulta de Buró de Crédito"
        title={"Tu Buró de Crédito está bloqueado"}
        subtitle="El acceso a tu historial crediticio está restringido. Necesitamos desbloquearlo para continuar con el análisis de tu solicitud."
      />

      <Paper variant="lendia_paper" sx={{ mt: 2, px: 4 }}>
        <Typography variant="body2" color="tokens.textSecondary" sx={{ lineHeight: 1.55 }}>
          Para continuar con tu solicitud, necesitamos que desbloquees el acceso a tu Buró de Crédito. Puedes hacerlo
          fácilmente desde el portal oficial.
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Callout tone="info" icon={<OpenInNew sx={{ fontSize: 16 }} />}>
            <Typography
              component="a"
              href={BURO_URL}
              target="_blank"
              rel="noreferrer"
              sx={{ color: "primary.main", fontSize: 13, wordBreak: "break-all" }}
            >
              {BURO_URL}
            </Typography>
          </Callout>
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <SwitchToggle checked={confirmUnlock} onChange={setConfirmUnlock} label="Ya desbloqueé mi Buró de Crédito" />
        </Box>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          disabled={!confirmUnlock || isLoading}
          onClick={handleClick}
          data-testid="buro-bloqueado-continuar-button"
        >
          Continuar
        </Button>
      </Box>
    </Box>
  );
};
